import React, {FC, useEffect, useMemo, useRef} from 'react';
import {DataFetcherRequest} from '../useRequest';
import {useIsConsentBannerAccepted} from '@aboutyou/storage';
import {GrpcTrailersConfig} from './useGenericGrpcRequest';
import {
  AB_TEST_HEADERS,
  // eslint-disable-next-line monorepo/no-internal-import -- direct imports breaks jest test with circular dependency
} from '@aboutyou/feature-flags/src/abtests/types';
import {useGrpcClientConfig} from './GrpcClientContext';
// eslint-disable-next-line monorepo/no-internal-import -- direct imports breaks jest test with circular dependency
import {usePrevious} from '@aboutyou/ui-components/src/hooks/usePrevious/usePrevious';

type RequestsWithPendingAbTestActivations = Map<
  string,
  {
    request: DataFetcherRequest<any, unknown>;
    possibleAbTests: string[];
    cacheKey: string;
  }
>;

type Context = React.MutableRefObject<RequestsWithPendingAbTestActivations>;

const ctx = React.createContext<Context | null>(null);

/**
 * We need to refetch the BE requests with pending ab test activations after the user accepts the cookie consent.
 * The pending activation are determined by the x-tadarida-possible-ab-tests trailer in each request.
 * If a request has a pending ab test activation based on the trailer and that AB Test is currently active for the user, then the request will be refetched with the updated config.
 */
export const RefetchRequestsWithPendingAbTestActivations: FC<
  React.PropsWithChildren
> = ({children}) => {
  const requestsWithPossibleAbTestsBeforeCookieConsent =
    useRef<RequestsWithPendingAbTestActivations>(new Map());

  const {hasUpdated, currentGrpcConfigAbTests} = useHasAbTestConfigUpdated();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (hasUpdated) {
      /**
       * Here we are refetching the requests with pending ab test activations after the user accepts the cookie consent.
       * that would mean the users has not seen the view where the abTest is activated yet.
       * We refetch those requests with the updated config. But this needs to happen only after the the useGrpcRequest instances has been updated with the new abTest config.
       *
       * We are using a setTimeout here to make sure the useGrpcRequest instances has been updated with the new abTest config.
       * This is not ideal because we cant be sure how long it will take for the useGrpcRequest instances to update.
       * But from a lot of manual testing it seems to work fine, even with cpu throttled simulation.
       */
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      timer = setTimeout(() => {
        currentGrpcConfigAbTests.forEach(abTest => {
          requestsWithPossibleAbTestsBeforeCookieConsent.current.forEach(
            entry => {
              if (entry.possibleAbTests.includes(abTest.key)) {
                entry.request.refetch();
              }
            },
          );
        });
      }, 300);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentGrpcConfigAbTests, hasUpdated]);

  return (
    <ctx.Provider value={requestsWithPossibleAbTestsBeforeCookieConsent}>
      {children}
    </ctx.Provider>
  );
};

export const useRememberRequestsWithPossibleAbTestsBeforeCookieConsent = ({
  request,
  cacheKey,
}: {
  cacheKey: string;
  request: DataFetcherRequest<any, unknown>;
}) => {
  const isConsentBannerAccepted = useIsConsentBannerAccepted();

  const requestsWithPossibleAbTestsBeforeCookieConsent = useContextValue();

  useEffect(() => {
    if (isConsentBannerAccepted) return;

    const possibleAbTestsFromGrpcTrailers = getPossibleAbTestsFromGrpcTrailers(
      (request as DataFetcherRequest<GrpcTrailersConfig>).data?.trailers,
    );
    if (!possibleAbTestsFromGrpcTrailers.length) return;

    requestsWithPossibleAbTestsBeforeCookieConsent.current.set(cacheKey, {
      request,
      possibleAbTests: possibleAbTestsFromGrpcTrailers,
      cacheKey,
    });
  }, [
    cacheKey,
    isConsentBannerAccepted,
    request,
    requestsWithPossibleAbTestsBeforeCookieConsent,
  ]);
};

const NOT_A_REAL_AB_TEST = 'ab_coupons_rollout'; // this is not a real ab_test, do not refetch the requests due to this, this ab_test would never get activated on the client

const useHasAbTestConfigUpdated = () => {
  const grpcConfig = useGrpcClientConfig();
  const currentGrpcConfigAbTests = useMemo(
    () => grpcConfig.abTests.filter(e => e.key !== NOT_A_REAL_AB_TEST),
    [grpcConfig],
  );
  const previousGrpcConfigAbTests = usePrevious(currentGrpcConfigAbTests);

  const previousAbTests = previousGrpcConfigAbTests?.map(e => e.key).join(',');
  const currentAbTests = currentGrpcConfigAbTests.map(e => e.key).join(',');

  return {
    hasUpdated: previousAbTests !== currentAbTests,
    currentGrpcConfigAbTests,
  };
};

const useContextValue = () => {
  const ctxValue = React.useContext(ctx);
  if (ctxValue === null) {
    throw new Error(
      'Missing context value. Please use the RefetchRequestsWithPendingAbTestActivations',
    );
  }
  return ctxValue;
};

const getPossibleAbTestsFromGrpcTrailers = (
  grpcTrailers?: GrpcTrailersConfig['trailers'],
) => {
  if (grpcTrailers && AB_TEST_HEADERS.TadaridaPossibleABTests in grpcTrailers) {
    return grpcTrailers[AB_TEST_HEADERS.TadaridaPossibleABTests] as string[];
  }

  return [];
};
